import React, { useState } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import staffService from "../../api/services/staffService";
import authService from "../../api/services/authService";

const RegisterBusinessForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    partnerName: "",
    password: "",
    password2: "",
    mobileNumber: "",
    contactNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    // PhoneInput returns a valid E.164 format or undefined
    return phone && phone.length >= 10;
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = "Email address is required";
    } else if (!validateEmail(formData.emailAddress)) {
      newErrors.emailAddress = "Please enter a valid email address";
    }

    if (!formData.partnerName.trim()) {
      newErrors.partnerName = "Partner name is required";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(formData.password)) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (!formData.password2) {
      newErrors.password2 = "Please confirm your password";
    } else if (formData.password !== formData.password2) {
      newErrors.password2 = "Passwords do not match";
    }

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!validatePhoneNumber(formData.mobileNumber)) {
      newErrors.mobileNumber = "Please enter a valid mobile number";
    }

    if (
      formData.contactNumber &&
      !validatePhoneNumber(formData.contactNumber)
    ) {
      newErrors.contactNumber = "Please enter a valid contact number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePhoneChange = (value, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value || "",
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for submission
      const submissionData = {
        ...formData,
        // Phone numbers are already in international format from PhoneInput
        mobileNumber: formData.mobileNumber,
        contactNumber: formData.contactNumber,
      };

      const tokenResp = await authService.initClient({
        staticToken: "TRGV34JKLOIVghjknuyh34421jKIVcyuvhj",
      });
      if (tokenResp.code === 0) {
        const resp = await staffService.partnerRegistration({
          ...submissionData,
          clientToken: tokenResp.clientToken,
        });
        if (resp.success) {
          alert("Business registration submitted successfully!");
          setFormData({
            firstName: "",
            lastName: "",
            emailAddress: "",
            partnerName: "",
            password: "",
            password2: "",
            mobileNumber: "",
            contactNumber: "",
          });
        } else {
          console.error("Error submitting form:", resp.message);
          alert(resp.message);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Error submitting registration. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === "password") {
      setShowPassword(!showPassword);
    } else if (field === "password2") {
      setShowPassword2(!showPassword2);
    }
  };

  return (
    <Card>
      <CardBody>
        <CardTitle tag="h4" className="mb-4">
          <i className="fas fa-building me-2"></i>
          Register New Business
        </CardTitle>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="firstName">
                  First Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="firstName"
                  id="firstName"
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  invalid={!!errors.firstName}
                />
                <FormFeedback>{errors.firstName}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="lastName">
                  Last Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="lastName"
                  id="lastName"
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  invalid={!!errors.lastName}
                />
                <FormFeedback>{errors.lastName}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="emailAddress">
                  Email Address <span className="text-danger">*</span>
                </Label>
                <Input
                  type="email"
                  name="emailAddress"
                  id="emailAddress"
                  placeholder="Enter email address"
                  value={formData.emailAddress}
                  onChange={handleInputChange}
                  invalid={!!errors.emailAddress}
                />
                <FormFeedback>{errors.emailAddress}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="partnerName">
                  Business Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="partnerName"
                  id="partnerName"
                  placeholder="Enter business name"
                  value={formData.partnerName}
                  onChange={handleInputChange}
                  invalid={!!errors.partnerName}
                />
                <FormFeedback>{errors.partnerName}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="password">
                  Password <span className="text-danger">*</span>
                </Label>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={handleInputChange}
                    invalid={!!errors.password}
                  />
                  <InputGroupText
                    style={{ cursor: "pointer" }}
                    onClick={() => togglePasswordVisibility("password")}
                  >
                    <i
                      className={`fas ${
                        showPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                    ></i>
                  </InputGroupText>
                </InputGroup>
                <FormFeedback
                  style={{ display: errors.password ? "block" : "none" }}
                >
                  {errors.password}
                </FormFeedback>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="password2">
                  Confirm Password <span className="text-danger">*</span>
                </Label>
                <InputGroup>
                  <Input
                    type={showPassword2 ? "text" : "password"}
                    name="password2"
                    id="password2"
                    placeholder="Confirm password"
                    value={formData.password2}
                    onChange={handleInputChange}
                    invalid={!!errors.password2}
                  />
                  <InputGroupText
                    style={{ cursor: "pointer" }}
                    onClick={() => togglePasswordVisibility("password2")}
                  >
                    <i
                      className={`fas ${
                        showPassword2 ? "fa-eye-slash" : "fa-eye"
                      }`}
                    ></i>
                  </InputGroupText>
                </InputGroup>
                <FormFeedback
                  style={{ display: errors.password2 ? "block" : "none" }}
                >
                  {errors.password2}
                </FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="mobileNumber">
                  Mobile Number <span className="text-danger">*</span>
                </Label>
                <PhoneInput
                  placeholder="Enter mobile number"
                  value={formData.mobileNumber}
                  onChange={(value) => handlePhoneChange(value, "mobileNumber")}
                  defaultCountry="US"
                  countryCallingCodeEditable={false}
                  className={
                    errors.mobileNumber ? "is-invalid" : "form-control"
                  }
                  style={{ display: "flex" }}
                />
                {errors.mobileNumber && (
                  <div className="invalid-feedback d-block">
                    {errors.mobileNumber}
                  </div>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="contactNumber">Store Number</Label>
                <PhoneInput
                  placeholder="Enter store number"
                  value={formData.contactNumber}
                  onChange={(value) =>
                    handlePhoneChange(value, "contactNumber")
                  }
                  defaultCountry="US"
                  countryCallingCodeEditable={false}
                  className={
                    errors.contactNumber ? "is-invalid" : "form-control"
                  }
                  style={{ display: "flex" }}
                />
                {errors.contactNumber && (
                  <div className="invalid-feedback d-block">
                    {errors.contactNumber}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12} className="text-center">
              <Button
                type="submit"
                color="primary"
                size="lg"
                disabled={isSubmitting}
                className="px-5"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2"></i>
                    {" Submit Registration"}
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
};

export default RegisterBusinessForm;
